// ClientSalesChart.js
// Chart showing sales value over time broken down by client

// Imports
import React from 'react';
import PropTypes from 'prop-types';
import { MenuItem, Tooltip, Button, Position, Checkbox } from '@blueprintjs/core';
import { MultiSelect } from '@blueprintjs/select';
import TimeSeriesChart, { aggregateByDay, processData } from './TimeSeriesChart';
import { ValueType, ChartMode, CHART_COLORS } from './chartEnums';

// Returns the total value of an archived order for a specific client
export function getClientArchivedTotal(order, clientId) {
  let total = 0;
  const clientData = order.byClient[clientId];
  if (!clientData) return 0;

  for (const key in clientData) {
    if (key === 'adjustment') {
      if (clientData.adjustment && clientData.adjustment.value) {
        total += clientData.adjustment.value;
      }
    } else {
      const product = clientData[key];
      if (product && product.price !== undefined) {
        total += product.price;
      }
    }
  }
  return total;
}

// Returns the total value of the open order for a specific client
export function getClientOpenTotal(currentOrder, products, clientId) {
  if (!currentOrder || !products || !products.byId || !currentOrder.byClient[clientId]) return 0;
  let total = 0;
  const clientData = currentOrder.byClient[clientId];

  for (const key in clientData) {
    if (key === 'adjustment') {
      if (clientData.adjustment && clientData.adjustment.value) {
        total += clientData.adjustment.value;
      }
    } else {
      const productOrder = clientData[key];
      const prod = products.byId[key];
      if (prod && productOrder) {
        const unitIdx = productOrder.unitId >= prod.price.length ? prod.price.length - 1 : productOrder.unitId;
        const price = productOrder.customPrice ? productOrder.customPrice : prod.price[unitIdx].price * productOrder.quantity;
        total += price;
      }
    }
  }
  return total;
}

// Converts orders into dataPoints for a single client
export function convertOrdersToClientDataPoints(orders, products, clientId, includeOpen = true) {
  const dataPoints = [];

  orders.archive.forEach(order => {
    if (order.dateClosed && order.byClient[clientId]) {
      dataPoints.push({
        x: new Date(order.dateClosed),
        y: getClientArchivedTotal(order, clientId),
      });
    }
  });

  if (includeOpen && orders.current && orders.current.byClient[clientId]) {
    dataPoints.push({
      x: new Date(),
      y: getClientOpenTotal(orders.current, products, clientId),
    });
  }

  return dataPoints;
}

// Returns a Set of client IDs that have at least one purchase
export function getClientsWithSales(orders) {
  const clientsWithSales = new Set();
  orders.archive.forEach(order => {
    Object.keys(order.byClient).forEach(clientId => clientsWithSales.add(Number(clientId)));
  });
  if (orders.current) {
    Object.keys(orders.current.byClient).forEach(clientId => clientsWithSales.add(Number(clientId)));
  }
  return clientsWithSales;
}

// Returns the top N client IDs by sales volume in the relevant time interval for the current mode
export function getTopClientsByMode(orders, products, mode, ytdYear, n, includeOpen = true) {
  const pointsByClient = {};
  const allClientIds = getClientsWithSales(orders);

  // Convert points for all active clients
  allClientIds.forEach(id => {
    pointsByClient[id] = convertOrdersToClientDataPoints(orders, products, id, includeOpen);
  });

  // Find the exact global years span to compute the top clients correctly for Yearly mode
  let globalYears = null;
  if (mode === ChartMode.YEARLY) {
    const yearsSet = new Set();
    allClientIds.forEach(id => {
      pointsByClient[id].forEach(pt => yearsSet.add(new Date(pt.x).getFullYear()));
    });
    globalYears = Array.from(yearsSet).map(Number).sort((a,b) => a-b);
  }

  // Calculate the total relevant value for each client based on mode
  const totals = Array.from(allClientIds).map(id => {
    const daily = aggregateByDay(pointsByClient[id]);
    const chartData = processData(daily, mode, undefined, ytdYear, globalYears);

    // Sum the values of the final interval bucket
    let relevantTotal = 0;
    if (chartData.values && chartData.values.length > 0) {
      if (mode === ChartMode.YEARLY) {
        // Top clients in the most recent year overall
        const targetYear = globalYears.length > 0
          ? Math.max(globalYears[globalYears.length - 1], new Date().getFullYear())
          : new Date().getFullYear();
        const yearIndex = chartData.labels.indexOf(String(targetYear));
        if (yearIndex >= 0) relevantTotal = chartData.values[yearIndex];
      } else if (mode === ChartMode.YTD) {
        // Top clients in the last month of target year
        relevantTotal = chartData.values[chartData.values.length - 1];
      } else if (mode === ChartMode.YTD_ACCUMULATED) {
        // Top clients in the YTD_ACCUMULATED total (sum of all months in the target year)
        relevantTotal = chartData.values.reduce((sum, v) => sum + v, 0);
      } else if (mode === ChartMode.WEEKLY) {
        // Top clients in the last 10 weeks combined
        relevantTotal = chartData.values.reduce((sum, v) => sum + v, 0);
      }
    }

    return { id, total: relevantTotal };
  });

  // Sort descending and take top N
  totals.sort((a, b) => b.total - a.total);
  return totals.slice(0, n).map(c => c.id);
}

// Component Class
class ClientSalesChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedClientIds: [],
      currentMode: ChartMode.YTD,
      currentYtdYear: null,
      includeOpen: true,
    };
  }

  // Returns name for the given client ID
  getClientName = (id) => {
    if (!id || !this.props.clients.byId[id]) return '';
    return this.props.clients.byId[id].name;
  }

  // MultiSelect tag renderer
  renderTag = (id) => {
    return this.getClientName(id);
  }

  // Clears a client from the selection when tag X is clicked
  handleTagRemove = (_tagLabel, index) => {
    this.setState(s => ({
      selectedClientIds: s.selectedClientIds.filter((_, i) => i !== index),
    }));
  }

  // Handles selecting/deselecting a client from the dropdown
  handleClientSelect = (id) => {
    this.setState(s => {
      const selected = s.selectedClientIds;
      if (selected.includes(id)) {
        return { selectedClientIds: selected.filter(selId => selId !== id) };
      } else {
        return { selectedClientIds: [...selected, id] };
      }
    });
  }

  // Predicate for filtering the dropdown via text
  filterClient = (query, id) => {
    const name = this.getClientName(id);
    return name.toLowerCase().includes(query.toLowerCase());
  }

  // Renders a client in the dropdown
  renderClientItem = (id, options) => {
    if (!options.modifiers.matchesPredicate) return null;
    const isSelected = this.state.selectedClientIds.includes(id);

    return (
      <MenuItem
        key={id}
        active={options.modifiers.active}
        icon={isSelected ? "tick" : "blank"}
        text={this.getClientName(id)}
        onClick={options.handleClick}
        shouldDismissPopover={false}
      />
    );
  }

  // Triggered by TimeSeriesChart when mode or year changes
  handleChartModeChange = (mode, ytdYear) => {
    this.setState({ currentMode: mode, currentYtdYear: ytdYear });
  }

  handleToggleOpen = (e) => {
    this.setState({ includeOpen: e.target.checked });
  }

  // Renders Component
  render() {
    const { orders, products, clients } = this.props;
    const { selectedClientIds, currentMode, currentYtdYear, includeOpen } = this.state;

    // Filter available clients dropdown to only show clients that have sales
    // and sort them alphabetically
    const clientsWithSales = getClientsWithSales(orders);
    const availableClients = clients.ids
      .filter(id => clientsWithSales.has(id))
      .sort((a, b) => this.getClientName(a).localeCompare(this.getClientName(b)));

    // Determine the active list of clients to graph
    let activeClientIds = selectedClientIds;
    if (activeClientIds.length === 0) {
      // Auto-fallback: top 5 by sales volume
      activeClientIds = getTopClientsByMode(orders, products, currentMode, currentYtdYear, 5, includeOpen);
    }

    // Build the Chart.js datasets array
    const datasets = activeClientIds.map((clientId, index) => ({
      label: this.getClientName(clientId) || `Cliente ${clientId}`,
      dataPoints: convertOrdersToClientDataPoints(orders, products, clientId, includeOpen),
      color: CHART_COLORS[index % CHART_COLORS.length],
    }));

    var info = (
      <div>
        É possível selecionar quantos clientes forem necessários.<br />
        Se nenhum cliente for selecionado, serão exibidos os 5 clientes com maior volume de vendas.<br />
      </div>
    );

    return (
      <div>
        <h3 style={{ textAlign: 'center' }} className="pt-heading">Vendas por Cliente</h3>
        <div style={{ marginBottom: '20px' }}>
          <div className="pt-label" style={{ marginBottom: '8px' }}>
            <Tooltip content={info} position={Position.RIGHT}>
              <Button rightIcon="info-sign" small minimal>
                <b>Selecione um ou mais Clientes</b>
              </Button>
            </Tooltip>
          </div>
          <MultiSelect
            items={availableClients}
            itemPredicate={this.filterClient}
            itemRenderer={this.renderClientItem}
            onItemSelect={this.handleClientSelect}
            tagRenderer={this.renderTag}
            tagInputProps={{
              onRemove: this.handleTagRemove,
              inputProps: {
                placeholder: 'Pesquisar...',
              },
            }}
            selectedItems={selectedClientIds}
            popoverProps={{ minimal: true }}
            resetOnSelect={true}
          />
        </div>
        <Checkbox 
          label="Incluir pedidos em aberto" 
          checked={includeOpen} 
          onChange={this.handleToggleOpen} 
          style={{ marginBottom: '10px' }}
        />
        <TimeSeriesChart
          datasets={datasets}
          valueType={ValueType.CURRENCY}
          onModeChange={this.handleChartModeChange}
          fillArea={false}
        />
      </div>
    );
  }
}

// Required Properties
ClientSalesChart.propTypes = {
  orders: PropTypes.object.isRequired,
  products: PropTypes.object.isRequired,
  clients: PropTypes.object.isRequired,
};

// Exports
export default ClientSalesChart;



// WEBPACK FOOTER //
// ./src/components/ChartView/ClientSalesChart.js